<template>
  <div class="pay-range-detail-page" :class="pageToneClass">
    <div v-if="state.loading" class="prdp-loading" role="status">
      <v-progress-circular indeterminate color="primary" size="40" />
      <p class="prdp-loading__text">Loading pay range…</p>
    </div>

    <div v-else-if="state.error" class="prdp-error">
      <p>{{ state.error }}</p>
      <button type="button" class="prdp-link" @click="goToList">
        Back to Pay Ranges
      </button>
    </div>

    <template v-else-if="state.card">
      <!-- ER-12492: Figma detail layout (header + versions timeline and list). -->
      <template v-if="newLayoutEnabled && detailView">
        <PayRangeDetailHeader
          :page-tone="detailView.pageTone"
          :version-tone="detailView.versionTone"
          :identity-segments="detailView.identitySegments"
          :displayed-version="detailView.displayedVersion"
          :archived-tag="detailView.archivedTag"
          :amount-label="detailView.amountLabel"
          :period-text="detailView.periodText"
          :readiness="detailView.readiness"
          :banner="detailView.banner"
          :menu-items="detailView.menuItems"
          :version-menu-items="detailView.versionMenuItems"
          @navigate-list="onCardEvent('navigate-list')"
          @open="onOpenDisplayedVersion"
          @menu-action="onCardEvent"
          @version-menu-action="onDisplayedVersionMenuAction"
        />
        <PayRangeTimelineAndList
          class="prdp-versions"
          :pay-ranges="[detailView.payRange]"
          :today="today"
          :read-only="detailView.readOnly"
          :selected-version-id="selectedVersionId"
          @version-select="onVersionSelect"
          @menu-action="onVersionMenuAction"
        />
      </template>
      <!-- ER-11243: version history renders inline inside the card, below the timeline. -->
      <PayRangeCard
        v-else
        v-bind="cardProps"
        :version-history="versionHistory"
        @correct="onCorrectVersion"
        @void="onVoidVersion"
        @open-version="onOpenVersion"
        @navigate-list="onCardEvent('navigate-list')"
        @discard-draft="onCardEvent('discard-draft')"
        @edit="onCardEvent('edit')"
        @publish="onCardEvent('publish')"
        @new-version="onCardEvent('new-version')"
        @edit-draft="onCardEvent('edit-draft')"
        @publish-draft="onCardEvent('publish-draft')"
        @archive="onCardEvent('archive')"
        @reactivate="onCardEvent('reactivate')"
        @unarchive="onCardEvent('unarchive')"
        @retire="onCardEvent('retire')"
        @more="onCardEvent('more')"
      />
    </template>

    <!-- Edit first DRAFT (scope still mutable): Draft drawer (Rule 33), legacy modal while idea-MDP-429 is off -->
    <template v-if="newLayoutEnabled">
      <PayRangeDraftDrawer
        v-if="state.editModal.show && state.api"
        :value="true"
        :pay-range="state.api"
        :step="state.editModal.stepNumber"
        :countries="draftDrawerCountries"
        :currencies="draftDrawerCurrencies"
        :currency-loading="draftDrawerCurrencyLoading"
        :region-items="draftDrawerRegionItems"
        :sub-region-items="draftDrawerSubRegionItems"
        :location-loading="draftDrawerLocationLoading"
        :job-families="draftDrawerJobFamilies"
        :pay-basis-options="draftDrawerPayBasisOptions"
        :work-mode-options="draftDrawerWorkModeOptions"
        :suggest="handleSuggestRange"
        :save-draft="handleDraftDrawerSave"
        :publish="handleDraftDrawerPublish"
        :discard="discardDraft"
        @search-job-family="searchDraftDrawerJobFamilies"
        @search-currency="searchDraftDrawerCurrencies"
        @search-location="searchDraftDrawerLocation"
        @clear-location="resetDraftDrawerLocationSearch"
        @save="onDraftDrawerSaved"
        @publish="onDraftDrawerPublished"
        @close="closeDraftDrawer"
      />
    </template>
    <PayRangeModal
      v-else
      :open="state.editModal.show"
      :mode="state.editModal.mode"
      :payRangeId="state.editModal.payRangeId"
      :stepNumber="state.editModal.stepNumber"
      :initialValues="state.editModal.initialValues"
      :saveDraft="handleEditSaveDraft"
      :suggestRange="handleSuggestRange"
      @onClose="closeEditModal"
      @closeAddDialog="closeEditModal"
    />

    <!-- New version / Edit draft: Version draft drawer (Rule 33), prefilled from the current version -->
    <PayRangeVersionProposalDrawer
      v-if="state.versionDraft.show && state.api"
      :value="true"
      :pay-range="state.api"
      :version="state.versionDraft.version"
      :replaced-version="state.versionDraft.replacedVersion"
      :save-draft="handleVersionDraftSave"
      :publish="handleVersionDraftPublish"
      :discard="handleVersionDraftDiscard"
      @save="onVersionDraftSaved"
      @publish="onVersionDraftPublished"
      @discard="onVersionDraftDiscarded"
      @close="closeVersionDraft"
    />

    <!-- Activate / publish first draft — Untitled UI (no Figma modal in ticket) -->
    <v-dialog
      :value="state.publishDialog.show"
      max-width="480"
      content-class="prdp-publish-dialog"
      @click:outside="closePublishDialog"
      @keydown.esc="closePublishDialog"
    >
      <div class="prdp-publish" role="dialog" aria-labelledby="prdp-publish-title">
        <header class="prdp-publish__header">
          <h2 id="prdp-publish-title" class="prdp-publish__title">
            Publish pay range
          </h2>
          <button
            type="button"
            class="prdp-publish__close"
            aria-label="Close"
            :disabled="state.saving"
            @click="closePublishDialog"
          >
            <img
              :src="require('@components/assets/x-close.svg')"
              alt=""
              width="20"
              height="20"
            />
          </button>
        </header>

        <div class="prdp-publish__body">
          <p class="prdp-publish__copy">
            Choose when
            <strong>{{ state.card?.name || "this pay range" }}</strong>
            becomes active.
          </p>

          <div class="prdp-publish__field">
            <label class="prdp-publish__label" for="prdp-effective-from"
              >Effective from</label
            >
            <div id="prdp-effective-from" class="prdp-publish__picker">
              <DatePicker
                :value="state.publishDialog.effectiveFrom"
                :modal="true"
                :customCalendarIcon="true"
                dataPickerPlaceHolder="Select date"
                @onApplyDate="onPublishDateApply"
              />
            </div>
          </div>
        </div>

        <footer class="prdp-publish__footer">
          <button
            type="button"
            class="prdp-publish__btn prdp-publish__btn--secondary"
            :disabled="state.saving"
            @click="closePublishDialog"
          >
            Cancel
          </button>
          <button
            type="button"
            class="prdp-publish__btn prdp-publish__btn--primary"
            :disabled="!state.publishDialog.effectiveFrom || state.saving"
            @click="confirmPublish"
          >
            {{ state.saving ? "Publishing…" : "Publish" }}
          </button>
        </footer>
      </div>
    </v-dialog>

    <!-- Retire published pay range -->
    <v-dialog
      :value="state.retireDialog.show"
      max-width="480"
      content-class="prdp-publish-dialog"
      @click:outside="closeRetireDialog"
      @keydown.esc="closeRetireDialog"
    >
      <div class="prdp-publish" role="dialog" aria-labelledby="prdp-retire-title">
        <header class="prdp-publish__header">
          <h2 id="prdp-retire-title" class="prdp-publish__title">
            Retire pay range
          </h2>
          <button
            type="button"
            class="prdp-publish__close"
            aria-label="Close"
            :disabled="state.saving"
            @click="closeRetireDialog"
          >
            <img
              :src="require('@components/assets/x-close.svg')"
              alt=""
              width="20"
              height="20"
            />
          </button>
        </header>

        <div class="prdp-publish__body">
          <p class="prdp-publish__copy">
            Choose when
            <strong>{{ state.card?.name || "this pay range" }}</strong>
            is retired. It can be archived or reactivated later.
          </p>

          <div class="prdp-publish__field">
            <label class="prdp-publish__label" for="prdp-retire-on"
              >Retire on</label
            >
            <div id="prdp-retire-on" class="prdp-publish__picker">
              <DatePicker
                :value="state.retireDialog.retireOn"
                :minDate="retireMinDate"
                :modal="true"
                :customCalendarIcon="true"
                dataPickerPlaceHolder="Select date"
                @onApplyDate="onRetireDateApply"
              />
            </div>
          </div>
        </div>

        <footer class="prdp-publish__footer">
          <button
            type="button"
            class="prdp-publish__btn prdp-publish__btn--secondary"
            :disabled="state.saving"
            @click="closeRetireDialog"
          >
            Cancel
          </button>
          <button
            type="button"
            class="prdp-publish__btn prdp-publish__btn--primary"
            :disabled="!canConfirmRetire || state.saving"
            @click="confirmRetire"
          >
            {{ state.saving ? "Retiring…" : "Retire" }}
          </button>
        </footer>
      </div>
    </v-dialog>

    <!-- Void published version (ER-11598) -->
    <v-dialog
      :value="state.voidDialog.show"
      max-width="480"
      content-class="prdp-publish-dialog"
      @click:outside="closeVoidDialog"
      @keydown.esc="closeVoidDialog"
    >
      <div class="prdp-publish" role="dialog" aria-labelledby="prdp-void-title">
        <header class="prdp-publish__header">
          <h2 id="prdp-void-title" class="prdp-publish__title">
            Void version
          </h2>
          <button
            type="button"
            class="prdp-publish__close"
            aria-label="Close"
            :disabled="state.saving"
            @click="closeVoidDialog"
          >
            <img
              :src="require('@components/assets/x-close.svg')"
              alt=""
              width="20"
              height="20"
            />
          </button>
        </header>

        <div class="prdp-publish__body">
          <p class="prdp-publish__copy">
            Void
            <strong>{{ voidVersionLabel }}</strong>
            of
            <strong>{{ state.card?.name || "this pay range" }}</strong>?
            It will stay in history as Voided and can no longer be used.
          </p>
        </div>

        <footer class="prdp-publish__footer">
          <button
            type="button"
            class="prdp-void__cancel prdp-publish__btn prdp-publish__btn--secondary"
            :disabled="state.saving"
            @click="closeVoidDialog"
          >
            Cancel
          </button>
          <button
            type="button"
            class="prdp-void__confirm prdp-publish__btn prdp-publish__btn--error"
            :disabled="state.saving"
            @click="confirmVoid"
          >
            {{ state.saving ? "Voiding…" : "Void" }}
          </button>
        </footer>
      </div>
    </v-dialog>

    <!-- ER-12490: version manage drawer (row / timeline bar click) -->
    <PayRangeVersionManageDrawer
      v-if="versionDrawerEnabled"
      :open="state.versionDrawer.show && !!manageVersion"
      :version="manageVersion"
      :versions="versionHistory"
      :range-status="state.card?._meta?.status || 'PUBLISHED'"
      :range-name="state.card?.name || ''"
      :code="state.card?.code || ''"
      :unit-suffix="state.card?.unitSuffix || 'USD/year'"
      :saving="state.saving"
      :chatter-enabled="true"
      @close="closeVersionDrawer"
      @correct="onManageCorrect"
      @void="onManageVoid"
      @new-version="onManageAction('new-version')"
      @edit-draft="onManageAction('edit-draft')"
      @discard-draft="onManageAction('discard-draft')"
      @publish-draft="onManageAction('publish-draft')"
      @chatter="onOpenVersionChatter"
    />

    <ChatterDrawer
      v-if="versionDrawerEnabled"
      :drawer="state.chatter.show"
      :iconName="state.chatter.iconName"
      :type="state.chatter.typeName"
      :typeValue="state.chatter.typeValue"
      :listData="state.chatter.listData"
      :chatterList="state.chatter.items"
      :totalCheckpointChatter="state.chatter.total"
      @closeDrawer="closeChatterDrawer"
      @requestMoreCheckpointsChatter="loadMoreChatter"
    />

    <CorrectPayRangeVersionModal
      :open="state.correctDialog.show"
      :version="state.correctDialog.version"
      :versions="versionHistory"
      :unit-suffix="state.card?.unitSuffix || 'USD/year'"
      :saving="state.saving"
      @close="closeCorrectDialog"
      @confirm="confirmCorrect"
    />
  </div>
</template>

<script setup>
import { computed, getCurrentInstance, onMounted, ref, toRef, watch } from "vue";
import PayRangeCard from "@components/PayRangeCard/PayRangeCard.vue";
import PayRangeDetailHeader from "@components/PayRangeDetailHeader/PayRangeDetailHeader.vue";
import PayRangeTimelineAndList from "@components/PayRangeTimelineAndList/PayRangeTimelineAndList.vue";
import { localTodayIso } from "@components/PayRangeTimelineAndList/payRangeTimelineUtils";
import { LD_FLAG_IDEA_MDP_429 } from "@components/constants/launchDarklyFlagKeys";
import PayRangeModal from "@components/PayRangeModal/PayRangeModal.vue";
import PayRangeDraftDrawer from "@components/PayRangeDraftDrawer/PayRangeDraftDrawer.vue";
import PayRangeVersionProposalDrawer from "@components/PayRangeVersionProposalDrawer/PayRangeVersionProposalDrawer.vue";
import DatePicker from "@components/DatePicker/DatePicker.vue";
import CorrectPayRangeVersionModal from "@components/CorrectPayRangeVersionModal/CorrectPayRangeVersionModal.vue";
import PayRangeVersionManageDrawer from "@components/PayRangeVersionManageDrawer/PayRangeVersionManageDrawer.vue";
import ChatterDrawer from "@components/Drawer/ChatterDrawer.vue";
import { suggestPayRange } from "@/api/payRangeAPI";
import { mapPayRangeDetailToView } from "./payRangeDetailView";
import {
  usePayRangeDetailPageState,
  toApiDateString,
  dayAfterApiDate,
} from "./composables/usePayRangeDetailPageState";
import { usePayRangeDraftDrawerOptions } from "./composables/usePayRangeDraftDrawerOptions";

const handleSuggestRange = (seed, options) => suggestPayRange(seed, options);

const props = defineProps({
  payRangeId: { type: [String, Number], required: true },
});

const emit = defineEmits(["version-select"]);

const { proxy } = getCurrentInstance() || {};
const newLayoutEnabled = ref(false);

onMounted(async () => {
  try {
    newLayoutEnabled.value = (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_429, false)) === true;
  } catch {
    newLayoutEnabled.value = false;
  }
});

const {
  state,
  versionHistory,
  goToList,
  onCardEvent,
  onCorrectVersion,
  closeCorrectDialog,
  confirmCorrect,
  onVoidVersion,
  onVersionMenuAction,
  closeVoidDialog,
  confirmVoid,
  manageVersion,
  versionDrawerEnabled,
  onOpenVersion,
  closeVersionDrawer,
  onManageCorrect,
  onManageVoid,
  onManageAction,
  onOpenVersionChatter,
  closeChatterDrawer,
  loadMoreChatter,
  closeEditModal,
  handleEditSaveDraft,
  handleDraftDrawerSave,
  handleDraftDrawerPublish,
  onDraftDrawerSaved,
  onDraftDrawerPublished,
  closeDraftDrawer,
  discardDraft,
  closePublishDialog,
  confirmPublish,
  closeRetireDialog,
  confirmRetire,
  handleVersionDraftSave,
  handleVersionDraftPublish,
  handleVersionDraftDiscard,
  onVersionDraftSaved,
  onVersionDraftPublished,
  onVersionDraftDiscarded,
  closeVersionDraft,
} = usePayRangeDetailPageState({
  payRangeId: toRef(props, "payRangeId"),
});

const {
  payBasisOptions: draftDrawerPayBasisOptions,
  workModeOptions: draftDrawerWorkModeOptions,
  jobFamilies: draftDrawerJobFamilies,
  countries: draftDrawerCountries,
  currencies: draftDrawerCurrencies,
  currencyLoading: draftDrawerCurrencyLoading,
  regionItems: draftDrawerRegionItems,
  subRegionItems: draftDrawerSubRegionItems,
  locationLoading: draftDrawerLocationLoading,
  loadCatalog: loadDraftDrawerCatalog,
  searchJobFamilies: searchDraftDrawerJobFamilies,
  searchCurrencies: searchDraftDrawerCurrencies,
  searchLocation: searchDraftDrawerLocation,
  resetLocationSearch: resetDraftDrawerLocationSearch,
} = usePayRangeDraftDrawerOptions();

watch(
  () => state.editModal.show,
  (open) => {
    if (open && newLayoutEnabled.value) loadDraftDrawerCatalog();
  }
);

/**
 * Retire-on must be after the current version's effective_from
 * (backend PAY_RANGE_RETIRE_ON_TOO_EARLY).
 */
const retireMinDate = computed(() =>
  dayAfterApiDate(state.card?._meta?.effectiveFrom)
);

const canConfirmRetire = computed(() => {
  const retireOn = toApiDateString(state.retireDialog.retireOn);
  if (!retireOn) return false;
  if (retireMinDate.value && retireOn < retireMinDate.value) return false;
  return true;
});

const voidVersionLabel = computed(() => {
  const version = state.voidDialog.version;
  if (!version) return "this version";
  if (version.versionLabel) return version.versionLabel;
  if (version.versionNumber != null) return `Version ${version.versionNumber}`;
  return "this version";
});

const today = localTodayIso();
const selectedVersionId = ref(null);

const detailView = computed(() => (state.api ? mapPayRangeDetailToView(state.api, today) : null));

const pageToneClass = computed(() =>
  newLayoutEnabled.value && detailView.value && detailView.value.pageTone !== "default"
    ? `pay-range-detail-page--${detailView.value.pageTone}`
    : ""
);

/** Row and bar clicks highlight the version and open its manage drawer (ER-12490). */
function onVersionSelect(version) {
  selectedVersionId.value = version?.uid ?? null;
  emit("version-select", version || null);
  if (version?.id == null) return;
  // A first draft has no version row to manage: its drawer is the Draft drawer (Rule 33).
  if (detailView.value?.cardState === "first-draft") onCardEvent("edit");
  else onOpenVersion({ key: version.id });
}

function onOpenDisplayedVersion() {
  const displayedId = detailView.value?.displayedVersion?.id;
  const version = detailView.value?.payRange.versions.find((item) => item.id === displayedId);
  if (version) {
    onVersionSelect({ ...version, uid: `${detailView.value.payRange.id}:${version.id}` });
  } else if (detailView.value?.cardState === "first-draft") {
    onCardEvent("edit");
  }
}

function onDisplayedVersionMenuAction(action) {
  const displayedId = detailView.value?.displayedVersion?.id;
  const version = detailView.value?.payRange.versions.find((item) => item.id === displayedId);
  if (version) onVersionMenuAction(version, action);
}

/** Strip private `_meta` before binding to PayRangeCard. */
const cardProps = computed(() => {
  const card = state.card;
  if (!card) return {};
  const { _meta, ...rest } = card;
  return rest;
});

function onPublishDateApply(date) {
  state.publishDialog.effectiveFrom = toApiDateString(date) || "";
}

function onRetireDateApply(date) {
  const next = toApiDateString(date) || "";
  if (retireMinDate.value && next && next < retireMinDate.value) {
    state.retireDialog.retireOn = retireMinDate.value;
    return;
  }
  state.retireDialog.retireOn = next;
}

defineExpose({
  state,
  confirmPublish,
  confirmRetire,
  confirmVoid,
  confirmCorrect,
  closeVersionDraft,
  openVersionDraft: () => onCardEvent("new-version"),
});
</script>

<style scoped>
.pay-range-detail-page {
  width: 100%;
  padding: 8px 0 32px;
}

.pay-range-detail-page--retired {
  background: #f2f4f7;
}

.pay-range-detail-page--archived {
  background: #fef3f2;
}

.prdp-versions {
  margin-top: 8px;
}

.prdp-loading,
.prdp-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: 240px;
  color: #475467;
  font-family: Inter, sans-serif;
  font-size: 14px;
}

.prdp-loading__text {
  margin: 0;
}

.prdp-link {
  border: none;
  background: transparent;
  color: #7f56d9;
  font-weight: 600;
  cursor: pointer;
}

/* Untitled UI modal — effective-date field */
.prdp-publish {
  background: #fff;
  border-radius: 12px;
  box-shadow:
    0 20px 24px -4px rgba(16, 24, 40, 0.08),
    0 8px 8px -4px rgba(16, 24, 40, 0.03);
  overflow: hidden;
}

.prdp-publish__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 24px 24px 0;
}

.prdp-publish__title {
  margin: 0;
  font-family: Inter, sans-serif;
  font-size: 18px;
  font-weight: 600;
  line-height: 28px;
  color: #101828;
}

.prdp-publish__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  margin: -8px -8px 0 0;
  padding: 0;
  border: none;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
}

.prdp-publish__close:hover {
  background: #f9fafb;
}

.prdp-publish__close:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.prdp-publish__body {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 16px 24px 8px;
}

.prdp-publish__copy {
  margin: 0;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #475467;
}

.prdp-publish__copy strong {
  color: #101828;
  font-weight: 600;
}

.prdp-publish__field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.prdp-publish__label {
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #344054;
}

.prdp-publish__picker {
  width: 100%;
}

.prdp-publish__picker ::v-deep .date-picker-display {
  width: 100%;
  justify-content: flex-start;
  gap: 8px;
  min-height: 44px;
  border-radius: 8px;
  border: 1px solid #d0d5dd;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
  padding: 10px 14px;
  background: #fff;
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  color: #101828;
}

.prdp-publish__picker ::v-deep .date-picker-display:hover {
  border-color: #98a2b3;
}

.prdp-publish__footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  padding: 24px;
  border-top: 1px solid #eaecf0;
}

.prdp-publish__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 100px;
  padding: 10px 16px;
  border-radius: 8px;
  border: 1px solid transparent;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
}

.prdp-publish__btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.prdp-publish__btn--secondary {
  background: #fff;
  border-color: #d0d5dd;
  color: #344054;
}

.prdp-publish__btn--secondary:hover:not(:disabled) {
  background: #f9fafb;
}

.prdp-publish__btn--primary {
  background: #7f56d9;
  border-color: #7f56d9;
  color: #fff;
}

.prdp-publish__btn--primary:hover:not(:disabled) {
  background: #6941c6;
  border-color: #6941c6;
}

.prdp-publish__btn--error {
  background: #d92d20;
  border-color: #d92d20;
  color: #fff;
}

.prdp-publish__btn--error:hover:not(:disabled) {
  background: #b42318;
  border-color: #b42318;
}
</style>

<style>
/* Dialog shell — content-class is applied outside scoped root */
.prdp-publish-dialog {
  box-shadow: none !important;
  background: transparent !important;
  border-radius: 12px !important;
  overflow: visible !important;
}
</style>
